import { CLOSE_CODE_NO_RECONNECT, type LobbyClientMessage, type LobbyServerMessage } from "@bn-games/network-protocol";

export type ConnectionState = "disconnected" | "connecting" | "connected";
export type ConnectionListener = (state: ConnectionState) => void;
export type MessageListener<TServer> = (message: TServer) => void;
export type TicketProvider = () => Promise<string | null>;

// Schedule spans the server's 5-minute reconnect window so a long blip doesn't
// leave the client stuck on "reconnecting" after the backoff exhausts but
// before the room is reaped. Total ~4 min across 8 attempts.
const RECONNECT_DELAYS_MS = [1000, 2000, 4000, 8000, 15000, 30000, 60000, 120000];
const PING_INTERVAL_MS = 25_000;
/**
 * Deadline on the ticket provider. `doConnect` awaits the provider *before*
 * constructing the socket, having already set state to "connecting" — so a
 * provider that never settles leaves no socket to fire `onclose`, no path into
 * `scheduleReconnect`, and a `connect()` guard that rejects every retry.
 *
 * Bounding it here rather than inside the provider is deliberate: a timeout on
 * the provider's own `fetch` only covers the response headers, leaving a
 * stalled body (a proxy that flushes headers early, a half-open connection)
 * able to hang the connection anyway. This bounds everything the provider
 * does, for every consumer, and degrades to an anonymous connection — the
 * outcome this method already treats as acceptable when a provider rejects.
 */
const TICKET_PROVIDER_TIMEOUT_MS = 8000;
const OUTBOUND_QUEUE_MAX = 50;

export interface ConnectionManagerOptions {
  /** Resolved WebSocket URL (e.g. `ws://localhost:3001/ws/backgammon`). */
  readonly serverUrl: string;
  /**
   * Room code to address on connect, sent as `?room=`. The Node server
   * ignores it; the Durable Object runtime routes on it, so any socket that
   * will carry a join/resume/reconnect must present it.
   */
  readonly room?: string;
}

/**
 * WebSocket lifecycle for one game namespace. Owns the raw socket, an
 * outbound queue for messages produced while disconnected, a ping/pong
 * heartbeat, and an exponential-backoff reconnect schedule.
 *
 * Generic over the app's client/server message unions. Apps' message unions
 * must include the lobby envelope (otherwise PING / lobby flow won't typecheck
 * at the consumer site).
 */
export class ConnectionManager<
  TClient = LobbyClientMessage,
  TServer = LobbyServerMessage,
> {
  private ws: WebSocket | null = null;
  private state: ConnectionState = "disconnected";
  private readonly serverUrl: string;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private shouldReconnect = false;
  private outboundQueue: TClient[] = [];
  private onlineHandler: (() => void) | null = null;

  private pendingTicket: string | null = null;
  private room: string | null = null;
  private ticketProvider: TicketProvider | null = null;
  private connectSequence = 0;

  private readonly stateListeners = new Set<ConnectionListener>();
  private readonly messageListeners = new Set<MessageListener<TServer>>();

  constructor(options: ConnectionManagerOptions) {
    this.serverUrl = options.serverUrl;
    this.room = options.room ?? null;
  }

  /**
   * Room the next (and every automatic backoff) connection addresses. Sticky
   * like the ticket: a dropped socket must reconnect to the same room, which
   * on the DO runtime means the same object.
   */
  setRoom(room: string | null): void {
    this.room = room;
  }

  setTicket(ticket: string | null): void {
    this.pendingTicket = ticket;
  }

  // For sockets meant to stay up for hours (e.g. a lobby socket): a static
  // ticket goes stale (60s TTL) long before a backoff reconnect retries, so
  // this re-invokes the provider fresh on every connection attempt instead.
  // Mutually exclusive with setTicket() in practice — provider wins if both
  // happen to be set.
  setTicketProvider(provider: TicketProvider | null): void {
    this.ticketProvider = provider;
  }

  getState(): ConnectionState {
    return this.state;
  }

  onStateChange(listener: ConnectionListener): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  onMessage(listener: MessageListener<TServer>): () => void {
    this.messageListeners.add(listener);
    return () => this.messageListeners.delete(listener);
  }

  connect(): void {
    if (this.state !== "disconnected") return;
    this.shouldReconnect = true;
    this.reconnectAttempt = 0;
    this.attachOnlineListener();
    void this.doConnect();
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.clearTimers();
    this.detachOnlineListener();
    this.outboundQueue = [];
    // Invalidates any doConnect() currently suspended awaiting the ticket
    // provider — it checks this on resume and bails out before touching ws.
    this.connectSequence += 1;
    // A deliberate teardown must not leak this ticket into a later, unrelated
    // connection that never calls setTicket() of its own (an anonymous join
    // after a signed-in session, say) — automatic backoff reconnects reuse
    // it without going through disconnect(), so this doesn't affect them.
    this.pendingTicket = null;
    // Same reasoning as the ticket: a deliberate teardown ends the room.
    this.room = null;
    if (this.ws) {
      // Detached before closing so the old socket's late close/message events
      // can't reach a socket opened by a same-tick connect() after this.
      this.detachHandlers(this.ws);
      this.ws.close();
      this.ws = null;
    }
    this.setState("disconnected");
  }

  private detachHandlers(socket: WebSocket): void {
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
  }

  send(message: TClient): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      return;
    }
    // Buffer until socket reopens. Drop oldest at cap so freshest intent wins.
    if (this.outboundQueue.length >= OUTBOUND_QUEUE_MAX) {
      this.outboundQueue.shift();
    }
    this.outboundQueue.push(message);
  }

  getQueueLength(): number {
    return this.outboundQueue.length;
  }

  /**
   * Runs the ticket provider under `TICKET_PROVIDER_TIMEOUT_MS`, resolving
   * `null` if it outlasts the deadline. The abandoned provider promise is left
   * to settle on its own — its result is simply discarded.
   */
  private async resolveTicket(provider: TicketProvider): Promise<string | null> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race<string | null>([
        provider(),
        new Promise<null>((resolve) => {
          timer = setTimeout(() => resolve(null), TICKET_PROVIDER_TIMEOUT_MS);
        }),
      ]);
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
  }

  private async doConnect(): Promise<void> {
    // Set synchronously, before any await: this is what makes connect()'s own
    // `state !== "disconnected"` guard reject a second concurrent connect()
    // call made while this attempt is suspended below fetching a ticket, and
    // it gives callers immediate "connecting" feedback instead of leaving
    // getState() reporting "disconnected" for the length of a network call.
    this.setState("connecting");
    const sequence = ++this.connectSequence;
    let ticket = this.pendingTicket;

    if (this.ticketProvider !== null) {
      try {
        ticket = await this.resolveTicket(this.ticketProvider);
      } catch {
        // A rejected provider degrades to an anonymous connection rather
        // than aborting the attempt — same reasoning as the stale-ticket
        // case below: the server just resolves it to no identity.
        ticket = null;
      }
      // A disconnect() (or a superseding reconnect) that ran while we were
      // suspended on the await above already tore down connection state —
      // resuming here must not go on to open a socket behind its back.
      if (sequence !== this.connectSequence) return;
    }

    let socket: WebSocket;
    try {
      // Deliberately NOT consumed here (pendingTicket path only — the
      // provider path re-invokes fresh above on every attempt instead): an
      // automatic backoff reconnect (the socket drops, ws.onclose schedules
      // a retry) goes straight through this same method without anyone
      // calling setTicket() again, so a signed-in player's identity would
      // silently disappear from every reconnect after the very first one.
      // Presenting a since-expired ticket is harmless — the server's
      // verifyWsTicket just resolves it to no identity, same as sending
      // none. disconnect() clears it instead, so a genuinely new session
      // never inherits a stale one.
      socket = new WebSocket(this.buildUrl(ticket));
    } catch {
      this.handleConnectionFailure();
      return;
    }
    this.ws = socket;
    // Every handler checks it still owns `this.ws`: a superseded socket (torn
    // down by disconnect(), replaced by a newer connect()) must not touch state.
    const isCurrent = (): boolean => this.ws === socket;

    socket.onopen = (): void => {
      if (!isCurrent()) return;
      this.reconnectAttempt = 0;
      this.flushQueue();
      this.setState("connected");
      this.startPing();
    };

    socket.onmessage = (event: MessageEvent): void => {
      if (!isCurrent()) return;
      try {
        const msg = JSON.parse(event.data as string) as TServer;
        if ((msg as { type: string }).type === "PONG") return;
        for (const listener of this.messageListeners) listener(msg);
      } catch {
        // Ignore malformed messages.
      }
    };

    socket.onclose = (event?: { readonly code?: number }): void => {
      if (!isCurrent()) return;
      this.stopPing();
      this.ws = null;
      // The server retired this socket on purpose; reconnecting would only
      // push the next-oldest one out in turn.
      if (event?.code === CLOSE_CODE_NO_RECONNECT) this.shouldReconnect = false;
      this.setState("disconnected");
      if (this.shouldReconnect) this.scheduleReconnect();
    };

    socket.onerror = (): void => {
      // onclose fires after onerror.
    };
  }

  private buildUrl(ticket: string | null): string {
    const params = new URLSearchParams();
    if (ticket !== null) params.set("ticket", ticket);
    if (this.room !== null) params.set("room", this.room);
    const query = params.toString();
    if (query === "") return this.serverUrl;
    // Plain concatenation rather than `new URL()`, which would normalise the
    // configured base (e.g. append a trailing slash to a bare host).
    const separator = this.serverUrl.includes("?") ? "&" : "?";
    return `${this.serverUrl}${separator}${query}`;
  }

  private handleConnectionFailure(): void {
    this.setState("disconnected");
    if (this.shouldReconnect) this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempt >= RECONNECT_DELAYS_MS.length) {
      this.shouldReconnect = false;
      return;
    }
    const delay =
      RECONNECT_DELAYS_MS[this.reconnectAttempt] ??
      RECONNECT_DELAYS_MS[RECONNECT_DELAYS_MS.length - 1] ??
      1000;
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.shouldReconnect) void this.doConnect();
    }, delay);
  }

  private flushQueue(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const queued = this.outboundQueue;
    this.outboundQueue = [];
    for (const msg of queued) this.ws.send(JSON.stringify(msg));
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      // PING is always in the lobby envelope, which every app's TClient
      // union includes — see the package contract above.
      this.send({ type: "PING" } as TClient);
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private clearTimers(): void {
    this.stopPing();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private setState(newState: ConnectionState): void {
    if (this.state === newState) return;
    this.state = newState;
    for (const listener of this.stateListeners) listener(newState);
  }

  // Restored connectivity: cancel any pending backoff and retry immediately,
  // resetting the attempt counter. Covers the common case where the browser
  // regains network well before the backoff schedule would have retried.
  private attachOnlineListener(): void {
    if (this.onlineHandler !== null) return;
    if (typeof window === "undefined" || typeof window.addEventListener !== "function") {
      return;
    }
    const handler = (): void => {
      if (!this.shouldReconnect) return;
      if (this.state === "connected" || this.state === "connecting") return;
      if (this.reconnectTimer !== null) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      this.reconnectAttempt = 0;
      void this.doConnect();
    };
    window.addEventListener("online", handler);
    this.onlineHandler = handler;
  }

  private detachOnlineListener(): void {
    if (this.onlineHandler === null) return;
    if (typeof window !== "undefined" && typeof window.removeEventListener === "function") {
      window.removeEventListener("online", this.onlineHandler);
    }
    this.onlineHandler = null;
  }
}
